import * as Sentry from '@sentry/react';

export type PaymentGatewayKey = 'adyen' | 'stripe' | 'paypal' | 'spreedly' | 'unknown';

type Primitive = string | number | boolean | null | undefined;
export type Attrs = Record<string, Primitive | object>;

const STORAGE_KEY = 'payment_log_context';

// Keep Sentry payloads small so payment events always ingest — large blobs (e.g. raw
// API `response` objects) otherwise bloat the envelope and get rejected with a 413.
const MAX_VALUE_LENGTH = 500;

function truncate(value: string): string {
  return value.length > MAX_VALUE_LENGTH ? `${value.slice(0, MAX_VALUE_LENGTH)}…` : value;
}

export const REDACTED = '[redacted]';

// Keys whose VALUE must never be logged, matched case-insensitively anywhere in the attrs tree.
//
// Callers routinely hand this logger a whole axios response or error — `paymentLog.error(..., {
// response })` appears at several sites — and an axios object carries `config.headers`, which
// carries `Authorization: Bearer <session token>`. So payment failures were shipping customer
// session tokens to Sentry and printing them to the console. Truncation at 500 chars was the only
// thing standing in the way, and that is incidental: it depends on where the header happens to fall
// in the serialised blob.
//
// Redacting centrally rather than at ~65 call sites is deliberate. A deny-list here cannot be
// forgotten by the next caller that passes a raw response, and no call site has to know which of
// its fields are sensitive.
const REDACTED_KEY_PATTERN =
  /^(authorization|cookie|set-cookie|x-otp-check-password|x-e2e-rate-limit-key)$|token|secret|password|api[-_]?key|document|cpf/i;

const MAX_REDACT_DEPTH = 6;

/**
 * Returns a structurally similar value with sensitive keys replaced.
 *
 * Cycle- and depth-guarded: axios objects cross-reference (`config` <-> `request`), so a naive walk
 * would recurse forever, and `JSON.stringify` throwing on the cycle later would have discarded the
 * whole attribute as '[unserializable]' rather than redacting it.
 */
function redact(value: unknown, seen = new WeakSet<object>(), depth = 0): unknown {
  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (seen.has(value as object) || depth >= MAX_REDACT_DEPTH) {
    return '[omitted]';
  }

  seen.add(value as object);

  if (Array.isArray(value)) {
    return value.map((item) => redact(item, seen, depth + 1));
  }

  const out: Record<string, unknown> = {};

  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    out[key] = REDACTED_KEY_PATTERN.test(key) ? REDACTED : redact(child, seen, depth + 1);
  }

  return out;
}

/** Scrubs an attrs bag while keeping objects browsable — used for the console, which is not Sentry. */
function redactAttrs(attrs: Attrs): Attrs {
  const out: Attrs = {};

  for (const [key, value] of Object.entries(attrs)) {
    out[key] = REDACTED_KEY_PATTERN.test(key) ? REDACTED : (redact(value) as Primitive | object);
  }

  return out;
}

/**
 * Flatten attrs for Sentry: primitives pass through (long strings truncated), objects are
 * REDACTED, then compactly serialized and truncated. Keeps tags/contexts/breadcrumbs lean,
 * 413-proof, and free of credentials.
 */
function safeAttrs(attrs: Attrs): Attrs {
  const out: Attrs = {};

  for (const [key, value] of Object.entries(attrs)) {
    if (REDACTED_KEY_PATTERN.test(key)) {
      out[key] = REDACTED;

      continue;
    }

    if (value === null || value === undefined || typeof value !== 'object') {
      out[key] = typeof value === 'string' ? truncate(value) : value;

      continue;
    }

    try {
      out[key] = truncate(JSON.stringify(redact(value)));
    } catch {
      out[key] = '[unserializable]';
    }
  }

  return out;
}

let baseContext: Attrs = {};

function readPersistedContext(): Attrs {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return {};
  }

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);

    return raw ? (JSON.parse(raw) as Attrs) : {};
  } catch {
    return {};
  }
}

function persistContext(ctx: Attrs): void {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }

  try {
    if (Object.keys(ctx).length === 0) {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ctx));
    }
  } catch {
    // ignore quota / disabled-storage failures
  }
}

function effectiveBaseContext(): Attrs {
  if (Object.keys(baseContext).length > 0) {
    return baseContext;
  }

  const persisted = readPersistedContext();
  if (Object.keys(persisted).length > 0) {
    baseContext = persisted;
  }

  return baseContext;
}

function tag(gateway: PaymentGatewayKey, attrs?: Attrs): Record<string, Primitive | object> {
  return {
    category: 'payment',
    gateway,
    ...effectiveBaseContext(),
    ...(attrs ?? {}),
  };
}

function format(gateway: PaymentGatewayKey, event: string): string {
  return `[payment.${gateway}] ${event}`;
}

function breadcrumb(gateway: PaymentGatewayKey, event: string, level: Sentry.SeverityLevel, attrs?: Attrs): void {
  Sentry.addBreadcrumb({
    category: `payment.${gateway}`,
    message: event,
    level,
    data: safeAttrs(tag(gateway, attrs)),
  });
}

type LogLevel = 'info' | 'warning' | 'error';

/** Stripe logs `payment_method`, Adyen `method_type`. */
function paymentMethod(attrs?: Attrs): string | undefined {
  const method = attrs?.payment_method ?? attrs?.method_type;

  return method != null ? String(method) : undefined;
}

/**
 * Every payment event goes out as a Sentry Log and a `payments.events` count, tagged like the
 * backend's PaymentLogger so both sides chart together.
 *
 * With Sentry running, the console line is skipped: `consoleLoggingIntegration` would ship it as a
 * second, unstructured log and the breadcrumbs integration as a second breadcrumb. Without Sentry
 * (local dev, tests) the console is the only place the event is visible, so it stays.
 */
function write(level: LogLevel, gateway: PaymentGatewayKey, event: string, attrs?: Attrs): void {
  breadcrumb(gateway, event, level, attrs);

  if (!Sentry.isInitialized()) {
    // `redactAttrs`, not the raw bag: the console is where the leak was actually observed. Objects
    // stay expandable (unlike safeAttrs, which stringifies for Sentry's size limits) so local
    // debugging is unaffected apart from the scrubbed fields.
    const log = { info: console.info, warning: console.warn, error: console.error }[level];

    log(format(gateway, event), redactAttrs(tag(gateway, attrs)));

    return;
  }

  const method = paymentMethod(attrs);
  const labels = { event, level, gateway, ...(method ? { payment_method: method } : {}) };

  Sentry.logger[level === 'warning' ? 'warn' : level](format(gateway, event), {
    ...safeAttrs(tag(gateway, attrs)),
    ...labels,
  } as Record<string, string | number | boolean>);
  Sentry.metrics.count('payments.events', 1, { attributes: labels });
}

const paymentLog = {
  info(gateway: PaymentGatewayKey, event: string, attrs?: Attrs): void {
    write('info', gateway, event, attrs);
  },
  warn(gateway: PaymentGatewayKey, event: string, attrs?: Attrs): void {
    write('warning', gateway, event, attrs);
  },

  /**
   * A failed payment step the customer hit: a decline, a refusal, a gateway or backend rejection,
   * a script the network did not deliver. Logged and counted, not raised as a Sentry issue — these
   * are expected outcomes, and an issue per occurrence buried the real frontend errors. Watch
   * `payments.events` for a spike instead.
   */
  error(gateway: PaymentGatewayKey, event: string, attrs?: Attrs): void {
    write('error', gateway, event, attrs);
  },

  /**
   * A failure only a code or contract change can fix — a response missing a field we rely on, an
   * SDK we mounted wrong, an exception thrown by our own code. Logged like `error`, and also raised
   * as a grouped Sentry issue: one fingerprint per (gateway, event), with the payment method as a
   * tag so search can filter wallets, e.g. payment_method:applepay.
   */
  issue(gateway: PaymentGatewayKey, event: string, attrs?: Attrs): void {
    write('error', gateway, event, attrs);

    const method = paymentMethod(attrs);

    Sentry.captureMessage(format(gateway, event), {
      level: 'error',
      tags: {
        payment_gateway: gateway,
        payment_event: event,
        ...(method ? { payment_method: method } : {}),
      },
      fingerprint: ['payment', gateway, event],
      contexts: { payment: safeAttrs(tag(gateway, attrs)) },
    });
  },

  /**
   * Merge fields into the base context attached to every subsequent log call.
   * Persists to sessionStorage so values survive third-party redirects (Adyen 3DS, etc.).
   */
  setContext(ctx: Attrs): void {
    baseContext = { ...effectiveBaseContext(), ...ctx };
    persistContext(baseContext);
  },

  /**
   * Drop all merged context (call after the checkout flow ends).
   */
  clearContext(): void {
    baseContext = {};
    persistContext({});
  },

  getContext(): Attrs {
    return { ...effectiveBaseContext() };
  },
};

export default paymentLog;
